import { FileType } from '@prisma/client';
import type { TRPCUtils } from '~/utils/trpc';
import { formatFileName } from './stringHelpers';
import { Canvg } from 'canvg';
import { dataExtensions, imageExtensions, videoExtensions } from '@schema/document.schema';
import axios from 'axios';
import { toUploadableImageFile } from './heicConversion';

export const convertBlobToBase64: (blob: Blob) => Promise<string> = (blob) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = reject;
		reader.onload = () => {
			const b64 = window.btoa(reader.result as string);
			resolve(b64);
		};
		reader.readAsBinaryString(blob);
	});

export const convertUrlToBase64: (url: string) => Promise<string> = (url) => {
	return convertUrlToBlob(url).then((b) => convertBlobToBase64(b));
};

export const convertUrlToBlob = (url: string) => {
	return fetch(url).then((r) => r.blob());
};

export type UploadDocumentResult = {
	fileName: string;
	s3Location: string;
	s3Bucket: string;
	s3Key: string;
	size: number;
	fileType: FileType;
	previewUrl: string;
};
const MULTIPART_PART_SIZE = 5 * 1024 * 1024; // 5MB — S3 minimum part size

/**
 * PUTs each part to its presigned URL and returns the ETags in part order.
 *
 * `concurrency` bounds how many parts are in flight. It defaults to all of them, which is what document
 * uploads have always done and is fine at a handful of parts. Callers uploading something large enough to
 * have dozens or hundreds of parts should set it — otherwise every part is queued at once and the ones at
 * the back can sit long enough to outlive their presigned URL.
 */
export async function performMultipartUpload(
	file: File,
	preSignedUploadUrls: string[],
	onProgress?: (percent: number) => void,
	concurrency: number = Number.POSITIVE_INFINITY
): Promise<string[]> {
	const partProgress = new Array(preSignedUploadUrls.length).fill(0);

	const uploadPart = (url: string, index: number) => {
		const start = index * MULTIPART_PART_SIZE;
		const end = Math.min(start + MULTIPART_PART_SIZE, file.size);
		const chunk = file.slice(start, end);

		return axios.put(url, chunk, {
			headers: { 'Content-Type': file.type || 'application/pdf' },
			onUploadProgress: (progressEvent) => {
				if (onProgress) {
					partProgress[index] = progressEvent.loaded ?? 0;
					const totalLoaded = partProgress.reduce((sum, b) => sum + b, 0);
					onProgress(Math.min(99, Math.round((totalLoaded / file.size) * 100)));
				}
			},
		});
	};

	if (concurrency >= preSignedUploadUrls.length) {
		const responses = await Promise.all(preSignedUploadUrls.map(uploadPart));
		return responses.map((r) => r.headers['etag'] as string);
	}

	const eTags = new Array<string>(preSignedUploadUrls.length);
	let next = 0;
	await Promise.all(
		Array.from({ length: concurrency }, async () => {
			while (next < preSignedUploadUrls.length) {
				const index = next++;
				const response = await uploadPart(preSignedUploadUrls[index]!, index);
				eTags[index] = response.headers['etag'] as string;
			}
		})
	);

	return eTags;
}

// Need new type for tRPC client
export async function uploadDocument({
	file: pickedFile,
	fileName: pickedFileName,
	trpcClient,
	jobId,
	quoteId,
	onProgress,
	multipart,
}: {
	file: File;
	fileName: string;
	trpcClient: TRPCUtils;
	jobId?: number;
	quoteId?: number;
	onProgress?: (percent: number) => void;
	multipart?: boolean;
}): Promise<UploadDocumentResult> {
	const file = await toUploadableImageFile(pickedFile);
	const fileName = file === pickedFile ? pickedFileName : file.name;
	const extension = fileName.split('.').pop();

	const matchFileType = extension?.toUpperCase();

	if (!matchFileType || !Object.values(FileType).includes(matchFileType as FileType)) {
		throw new Error('File Type not supported');
	}

	const formattedFileName = formatFileName(fileName);

	// Multipart is only supported for job uploads (not quotes) and only worthwhile above the minimum part size
	if (multipart && jobId && file.size > MULTIPART_PART_SIZE) {
		const numberOfParts = Math.ceil(file.size / MULTIPART_PART_SIZE);
		const multipartDest = await trpcClient.documents.generateMultiPartPreSignedUploadUrls.mutate({
			fileName: formattedFileName,
			jobId,
			numberOfParts,
		});

		let eTags: string[];
		try {
			eTags = await performMultipartUpload(file, multipartDest.preSignedUploadUrls, onProgress);
		} catch (error) {
			console.error(error);
			console.error('Aborting multipart upload, there was an error');
			await trpcClient.documents.abortMultiPartUploadCommand
				.mutate({ jobId, key: multipartDest.s3Key, bucket: multipartDest.s3Bucket, uploadId: multipartDest.uploadId })
				.catch(() => {});
			throw error;
		}

		const [previewUrl] = await Promise.all([
			trpcClient.documents.generatePreSignedPreviewUrl.mutate({
				bucket: multipartDest.s3Bucket,
				key: multipartDest.s3Key,
				fileType: matchFileType as FileType,
			}),
			trpcClient.documents.completeMultiPartUploadCommand.mutate({
				jobId,
				key: multipartDest.s3Key,
				bucket: multipartDest.s3Bucket,
				uploadId: multipartDest.uploadId,
				eTags,
			}),
		]);

		onProgress?.(100);

		return {
			fileName: multipartDest.fileName,
			s3Location: multipartDest.s3Location,
			s3Bucket: multipartDest.s3Bucket,
			s3Key: multipartDest.s3Key,
			size: file.size,
			fileType: matchFileType as FileType,
			previewUrl,
		};
	}

	let s3Destination: {
		preSignedUploadUrl: string;
		fileName: string;
		s3Location: string;
		s3Bucket: string;
		s3Key: string;
	};
	if (jobId) {
		s3Destination = await trpcClient.documents.generatePreSignedUrl.mutate({
			fileName: formattedFileName,
			jobId,
		});
	} else {
		if (!quoteId) {
			throw new Error('Need a job id or a quote id to upload a document');
		}

		s3Destination = await trpcClient.documents.generatePreSignedUrlForQuote.mutate({
			fileName: formattedFileName,
			quoteId,
		});
	}

	const [previewUrl, uploadResponse] = await Promise.all([
		trpcClient.documents.generatePreSignedPreviewUrl.mutate({
			bucket: s3Destination.s3Bucket,
			key: s3Destination.s3Key,
			fileType: matchFileType as FileType,
		}),
		axios.put(s3Destination.preSignedUploadUrl, file, {
			headers: {
				'Content-Type': file.type || 'application/pdf',
			},
			onUploadProgress: (progressEvent) => {
				if (onProgress && progressEvent.progress) {
					onProgress(Math.round(progressEvent.progress * 100));
				}
			},
		}),
	]);

	if (uploadResponse.status !== 200) {
		throw new Error('There was a problem uploading the file to S3');
	}

	return {
		...s3Destination,
		size: file.size,
		fileType: matchFileType as FileType,
		previewUrl,
	};
}

// For quotes
export async function customerUploadDocument(
	file: File,
	fileName: string,
	jobId: number,
	estimateId: number,
	uniqueAccessId: string,
	trpcClient: TRPCUtils
): Promise<UploadDocumentResult> {
	const extension = fileName.split('.').pop();

	const matchFileType = extension?.toUpperCase();

	if (!matchFileType || !Object.values(FileType).includes(matchFileType as FileType)) {
		throw new Error('File Type not supported');
	}

	const formattedFileName = formatFileName(fileName);

	const s3Destination = await trpcClient.customerQuote.documentGeneratePreSignedUrl.mutate({
		fileName: formattedFileName,
		jobId,
		estimateId,
		uniqueAccessId,
	});

	const previewUrl = await trpcClient.customerQuote.documentGeneratePreSignedPreviewUrl.mutate({
		bucket: s3Destination.s3Bucket,
		key: s3Destination.s3Key,
		fileType: matchFileType as FileType,
		jobId,
		estimateId,
		uniqueAccessId,
	});

	const uploadResponse = await fetch(s3Destination.preSignedUploadUrl, {
		method: 'PUT',
		body: file,
	});

	if (uploadResponse.status != 200) {
		throw new Error('There was a problem uploading the file to S3');
	}

	return {
		...s3Destination,
		size: file.size,
		fileType: matchFileType as FileType,
		previewUrl,
	};
}

export async function customerUploadSignedPDF({
	pdfFile,
	fileName,
	customerId,
	uniqueAccessId,
	originalDocumentId,
	trpcClient,
}: {
	pdfFile: File;
	fileName: string;
	customerId: number;
	uniqueAccessId: string;
	originalDocumentId: number;
	trpcClient: TRPCUtils;
}): Promise<Omit<UploadDocumentResult, 'previewUrl'>> {
	const extension = fileName.split('.').pop();

	const matchFileType = extension?.toUpperCase();

	if (!matchFileType || !Object.values(FileType).includes(matchFileType as FileType)) {
		throw new Error('File Type not supported');
	}

	const formattedFileName = formatFileName(fileName);

	const s3Destination = await trpcClient.documents.generateUploadUrlForCustomer.mutate({
		customerId,
		uniqueAccessId,
		originalDocumentId,
		fileName: formattedFileName,
	});

	const uploadResponse = await fetch(s3Destination.preSignedUploadUrl, {
		method: 'PUT',
		body: pdfFile,
	});

	if (uploadResponse.status != 200) {
		throw new Error('There was a problem uploading the file to S3');
	}

	return {
		...s3Destination,
		size: pdfFile.size,
		fileType: matchFileType as FileType,
	};
}

export async function uploadDocumentForMobile(
	file: File,
	fileName: string,
	trpcClient: TRPCUtils,
	mobileKey: string,
	clerkUserId: string,
	jobId: number,
	quoteId?: number
): Promise<UploadDocumentResult> {
	const extension = fileName.split('.').pop();

	const matchFileType = extension?.toUpperCase();

	if (!matchFileType || !Object.values(FileType).includes(matchFileType as FileType)) {
		throw new Error('File Type not supported');
	}

	const formattedFileName = formatFileName(fileName);

	const s3Destination = await trpcClient.documents.generatePreSignedUrlForMobile.mutate({
		fileName: formattedFileName,
		jobId,
		mobileKey,
		clerkUserId,
	});

	const previewUrl = await trpcClient.documents.generatePreSignedPreviewUrlForMobile.mutate({
		bucket: s3Destination.s3Bucket,
		key: s3Destination.s3Key,
		fileType: matchFileType as FileType,
		mobileKey,
		clerkUserId,
	});

	const uploadResponse = await fetch(s3Destination.preSignedUploadUrl, {
		method: 'PUT',
		body: file,
	});

	if (uploadResponse.status != 200) {
		throw new Error('There was a problem uploading the file to S3');
	}

	return {
		...s3Destination,
		size: file.size,
		fileType: matchFileType as FileType,
		previewUrl,
	};
}

export const getFileIcon = (fileType: string | FileType) => {
	let icon = 'file';
	let type = fileType;
	if (fileType === FileType.CSV) {
		icon = 'file-csv';
	} else if (fileType === FileType.PDF) {
		icon = 'file-pdf';
	} else if (imageExtensions.includes(fileType as FileType)) {
		icon = 'file-image';
		type = 'image';
	} else if (dataExtensions.includes(fileType)) {
		icon = 'table-list';
	} else if (videoExtensions.includes(fileType)) {
		icon = 'file-video';
		type = 'video';
	}

	return { icon: icon, type: type };
};

export const fileExistsAtUrl = (fileURL: string) => {
	const http = new XMLHttpRequest();

	http.open('HEAD', fileURL, false);
	http.send();

	return http.status === 200;
};

export const blobToPDFFile = (blobData: Blob, fileName: string): File => {
	return new File([blobData], fileName, { type: 'application/pdf' });
};

export const domElementToImage = async (element: Node, canvas: OffscreenCanvas, fileName: string, type: string): Promise<File> => {
	const domString = new XMLSerializer().serializeToString(element);
	const ctx = canvas.getContext('2d');
	const v = await Canvg.from(ctx!, domString);
	await v.render();
	const blob = await canvas.convertToBlob();
	return new File([blob], fileName, { type });
};

export const downloadContentAsFile = (input: { uriEncodedContent: string; fileName: string }) => {
	const element = document.createElement('a');

	element.setAttribute('href', input.uriEncodedContent);
	element.setAttribute('download', input.fileName);
	element.style.display = 'none';

	document.body.appendChild(element);

	element.click();

	document.body.removeChild(element);
};
